import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import newsService from '../../services/newsServices';
import { Article } from '../../types/news.types';
import { getCategoryColor, getCategoryLabel } from '../../utils/categories';
import { formatDate, formatTimeAgo } from '../../utils/dateFormatter';
import styles from './Category.module.css';

const Category: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalArticles, setTotalArticles] = useState(0);
  const limit = 10;

  useEffect(() => {
    // Reset when category changes
    setArticles([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [category]);

  useEffect(() => {
    const fetchArticles = async () => {
      if (!category) return;

      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        console.log(`📂 Fetching ${category} articles - Page ${page}`);
        const response = await newsService.getArticlesByCategory(category, page, limit);
        
        console.log('✅ Articles fetched:', response.articles.length);
        console.log('📊 Pagination:', response.pagination);

        if (page === 1) {
          setArticles(response.articles);
        } else {
          setArticles((prev) => [...prev, ...response.articles]);
        }

        setTotalArticles(response.pagination.totalArticles);
        setHasMore(response.pagination.hasNextPage);
        setLoading(false);
        setLoadingMore(false);

        // Scroll to top only on first page
        if (page === 1) {
          window.scrollTo(0, 0);
        }
      } catch (err: any) {
        console.error('❌ Failed to fetch articles:', err);
        setError(err.message || 'Failed to load articles');
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchArticles();
  }, [category, page]);

  const loadMore = () => {
    if (hasMore && !loadingMore) {
      setPage((prev) => prev + 1);
    }
  };

  // Loading state (first load)
  if (loading && page === 1) {
    return (
      <div className={styles.categoryPage}>
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading {getCategoryLabel(category || '')} articles...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && page === 1) {
    return (
      <div className={styles.categoryPage}>
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>⚠️</div>
            <h2>Oops! Something went wrong</h2>
            <p className={styles.errorMessage}>{error}</p>
            <button className={styles.retryButton} onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.categoryPage}>
      <div className={styles.container}>
        {/* Header with Breadcrumb */}
        <div className={styles.breadcrumb}>
          <Link to="/">Home</Link>
          <span className={styles.separator}>/</span>
          <span className={styles.current}>{getCategoryLabel(category || '')}</span>
        </div>

        {/* Category Header */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div
              className={styles.categoryBadge}
              style={{ backgroundColor: getCategoryColor(category || '') }}
            >
              {getCategoryLabel(category || '')}
            </div>
            <h1 className={styles.title}>
              {getCategoryLabel(category || '')}
            </h1>
            <p className={styles.subtitle}>
              {totalArticles > 0 
                ? `${totalArticles} ${totalArticles === 1 ? 'article' : 'articles'} found`
                : 'Explore our latest articles'}
            </p>
          </div>
        </header>

        {/* Articles List */}
        {articles.length > 0 ? (
          <>
            <div className={styles.articlesList}>
              {articles.map((article, index) => (
                <Link
                  key={`${article.id}-${index}`}
                  to={`/news/${article.slug}`}
                  className={styles.articleCard}
                >
                  {/* Image */}
                  <div className={styles.imageWrapper}>
                    <img
                      src={article.featuredImage.url}
                      alt={article.featuredImage.alt || article.headline}
                      loading="lazy"
                    />
                    <div className={styles.imageOverlay}></div>
                  </div>

                  {/* Content */}
                  <div className={styles.cardContent}>
                    {/* Category Badge */}
                    <span
                      className={styles.cardCategoryBadge}
                      style={{ backgroundColor: getCategoryColor(article.category) }}
                    >
                      {getCategoryLabel(article.category)}
                    </span>

                    {/* Headline */}
                    <h2 className={styles.cardTitle}>{article.headline}</h2>

                    {/* Excerpt */}
                    {article.excerpt && (
                      <p className={styles.cardExcerpt}>{article.excerpt}</p>
                    )}

                    {/* Meta Info */}
                    <div className={styles.cardMeta}>
                      <div className={styles.authorInfo}>
                        {article.author?.profilePhoto && (
                          <img
                            src={article.author?.profilePhoto}
                            alt={article.author?.name || 'Unknown'}
                            className={styles.authorAvatar}
                          />
                        )}
                        <div className={styles.authorDetails}>
                          <span className={styles.authorName}>{article.author?.name || 'Unknown'}</span>
                          <div className={styles.metaStats}>
                            <time dateTime={article.publishedAt || article.createdAt}>
                              {formatDate(article.publishedAt || article.createdAt)}
                            </time>
                            <span className={styles.separator}>•</span>
                            <span>{article.readTime} min read</span>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className={styles.cardStats}>
                        <span className={styles.stat}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                          </svg>
                          {article.views.toLocaleString()}
                        </span>
                        <span className={styles.stat}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          {article.likes}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className={styles.loadMoreWrapper}>
                <button
                  className={styles.loadMoreButton}
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className={styles.smallSpinner}></div>
                      Loading more...
                    </>
                  ) : (
                    <>
                      Load More Articles
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M19 9l-7 7-7-7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </button>
                <p className={styles.loadMoreInfo}>
                  Showing {articles.length} of {totalArticles} articles
                </p>
              </div>
            )}

            {/* End Message */}
            {!hasMore && articles.length > 0 && (
              <div className={styles.endMessage}>
                <div className={styles.endIcon}>🎉</div>
                <p>You've reached the end!</p>
                <p className={styles.endSubtext}>
                  That's all {totalArticles} articles in {getCategoryLabel(category || '')}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📰</div>
            <h3>No Articles Found</h3>
            <p>There are no articles in this category yet.</p>
            <Link to="/" className={styles.backHomeButton}>
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Category;